import React from 'react';
import { useTheme } from '@/hooks/useTheme';
import { BodyText } from '@/components/ui/BodyText';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { TitleText } from '@/components/ui/TitleText';

export interface AddIngredientsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (mode: 'append' | 'replace') => void | Promise<void>;
  mealCount: number;
  ingredientCount: number;
  isSubmitting?: boolean;
}

/**
 * Modal to confirm adding ingredients from a meal plan to the shopping list.
 * Offers "Keep existing (append)" and "Replace" actions for merge behavior.
 */
export const AddIngredientsModal: React.FC<AddIngredientsModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isSubmitting = false,
}) => {
  const { isDark } = useTheme();

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Overlay */}
      <div
        className={`absolute inset-0 ${isDark ? 'bg-black/70' : 'bg-black/50'}`}
        onClick={onClose}
      />

      <Card
        className={`relative w-full max-w-lg mx-4 shadow-xl ${
          isDark ? 'bg-neutral-900 border-neutral-700' : 'bg-white'
        }`}
      >
        <div className="p-6 space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <TitleText>Add ingredients to Shopping List</TitleText>
              <BodyText variant="muted">
                Your shopping list is not empty, would you like to empty your shopping list before adding these meals?
              </BodyText>
            </div>
            <button
              onClick={onClose}
              className={`p-2 rounded-md border transition-colors ${
                isDark
                  ? 'border-neutral-500 hover:bg-neutral-800'
                  : 'border-neutral-200 hover:bg-neutral-100'
              }`}
              aria-label="Close modal"
            >
              <svg
                className={`w-5 h-5 ${isDark ? 'text-neutral-200' : 'text-neutral-700'}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-3">
            <Button
              variant="primary"
              className="w-full justify-center"
              onClick={() => onConfirm('append')}
              disabled={isSubmitting}
              loading={isSubmitting}
            >
              Keep
            </Button>
            <Button
              variant="secondary"
              className="w-full justify-center"
              onClick={() => onConfirm('replace')}
              disabled={isSubmitting}
              loading={isSubmitting}
            >
              Replace
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AddIngredientsModal;
